import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ExcelTemplate, templateService } from "@/lib/services/templateService";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

interface Props {
  onSelect: (template: ExcelTemplate) => void;
}

export const TemplateSelector = ({ onSelect }: Props) => {
  const [templates, setTemplates] = useState<ExcelTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await templateService.getAllTemplates();
      setTemplates(data);
    } catch (error) {
      console.error("Error loading templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (id: string, currentActive: boolean) => {
    try {
      await templateService.setTemplateActive(id, !currentActive);
      await loadTemplates();
      toast.success(
        currentActive ? "Template deactivated" : "Template activated",
      );
    } catch (error) {
      toast.error("Failed to update template status");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await templateService.deleteTemplate(id);
      await loadTemplates();
      toast.success("Template deleted");
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading templates...</div>;
  }

  return (
    <div className="space-y-4">
      {templates.length === 0 ? (
        <p className="text-gray-500 text-center py-4">
          No saved templates found
        </p>
      ) : (
        templates.map((template) => (
          <Card
            key={template.id}
            className={template.is_active ? "border-green-500" : ""}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">{template.name}</h3>
                  <p className="text-sm text-gray-600">
                    {template.description}
                  </p>
                  <p className="text-xs text-gray-400">
                    File: {template.file_name} | Version: {template.version} |
                    Created:{" "}
                    {new Date(template.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onSelect(template)}
                  >
                    Use Template
                  </Button>

                  <Button
                    className="bg-primary-blue"
                    variant={template.is_active ? "secondary" : "default"}
                    size="sm"
                    onClick={() =>
                      handleActivate(template.id, template.is_active)
                    }
                  >
                    {template.is_active ? "Deactivate" : "Activate"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(template.id)}
                    className="text-red-500 hover:text-red-700 hover:border-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};
